import { apiClient as api } from "./httpClient";
import { Institution, InstitutionCatalogs } from "./institutionsService";

// Vínculos do paciente (RN-553): onde ele estuda, trabalha, treina, é atendido ou frequenta.
// Aba com permissão própria `patient-links`; quem vê a aba também escreve. Encerrar um vínculo é
// preencher a data de fim — não existe flag de ativo, a vigência é a verdade.

export interface PatientLink {
  id: string;
  linkTypeId: string;
  linkTypeName: string;
  institutionId: string;
  institutionName: string;
  /** Telefone geral da instituição (o do contato do dia a dia é o contactPhone) */
  institutionPhone: string | null;
  description: string | null;
  /** yyyy-MM-dd */
  startDate: string | null;
  endDate: string | null;
  /** Derivado da vigência no servidor: sem data de fim, ou fim ainda no futuro */
  current: boolean;
  notes: string | null;
  contactName: string | null;
  contactPhone: string | null;
}

export interface PatientLinkTypeOption {
  id: string;
  name: string;
  order: number;
}

export interface SavePatientLinkPayload {
  id?: string | null;
  patientId: string;
  linkTypeId: string;
  institutionId: string;
  description?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  notes?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
}

export const PATIENT_LINK_NOTES_MAX_LENGTH = 1000;

export const patientLinksService = {
  /** status: 'current' (default) | 'ended' | 'all' */
  getByPatient: async (patientId: string, status = "current"): Promise<PatientLink[]> => {
    const response = await api.get(`/patient-links/by-patient/${patientId}`, { params: { status } });
    return response.data;
  },

  types: async (): Promise<PatientLinkTypeOption[]> => {
    const response = await api.get("/patient-links/types");
    return response.data;
  },

  save: async (payload: SavePatientLinkPayload): Promise<string> => {
    const response = await api.post("/patient-links", payload);
    return response.data?.id;
  },

  /** Exclusão lógica — para o registro feito por engano. Encerrar é datar o fim. */
  remove: async (id: string): Promise<void> => {
    await api.delete(`/patient-links/${id}`);
  },

  // As três abaixo existem sob a permissão da ABA (RN-553k). A rota do cadastro exige
  // `institutions`, que o Profissional não tem — por ela, o combo abriria vazio para ele.
  institutions: async (entityId: string, search?: string): Promise<Institution[]> => {
    const response = await api.get(`/patient-links/institutions/${entityId}`, {
      params: { search: search || undefined },
    });
    return response.data;
  },

  institutionCatalogs: async (entityId: string): Promise<InstitutionCatalogs> => {
    const response = await api.get(`/patient-links/institution-catalogs/${entityId}`);
    return response.data;
  },

  /** Inclusão rápida: só CRIA. Editar e inativar seguem na tela de Instituições. */
  quickAddInstitution: async (payload: {
    entityId: string;
    name: string;
    phone?: string | null;
    natureId?: string | null;
    sphereId?: string | null;
    segmentId?: string | null;
    kind?: string | null;
    professionalTypeId?: string | null;
    professionalTypeOther?: string | null;
  }): Promise<string> => {
    const response = await api.post("/patient-links/institutions", payload);
    return response.data?.id;
  },
};

export const mapPatientLinkError = (error: any): string => {
  const code: string = error?.response?.data?.code ?? "";
  const messages: Record<string, string> = {
    "PatientLinks.NotFound": "Vínculo não encontrado.",
    "PatientLinks.PatientNotFound": "Paciente não encontrado nesta entidade.",
    "PatientLinks.LinkTypeRequired": "Informe o tipo de vínculo.",
    "PatientLinks.LinkTypeNotFound": "Tipo de vínculo não encontrado ou inativo.",
    "PatientLinks.InstitutionRequired": "Informe a instituição.",
    "PatientLinks.InvalidPeriod": "A data de fim não pode ser anterior à de início.",
    "PatientLinks.Duplicated":
      "Este paciente já tem um vínculo em curso com esta instituição neste tipo.",
    "Institutions.NotFound": "Instituição não encontrada ou inativa.",
  };
  return messages[code] || "Não foi possível concluir a operação. Tente novamente.";
};
